import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import {
  Mail, KeyRound, ShieldCheck, Activity, CheckCircle, AlertCircle,
  LogIn, LogOut, UserX, Ban
} from 'lucide-react';

const eventConfig = {
  login_success: { label: 'Successful login', icon: LogIn, color: '#22c55e' },
  login_failure: { label: 'Failed login', icon: AlertCircle, color: '#ef4444' },
  login_blocked: { label: 'Blocked (rate-limited)', icon: Ban, color: '#f97316' },
  login_denied_role: { label: 'Denied — non-admin role', icon: UserX, color: '#dc2626' },
  password_change: { label: 'Password changed', icon: KeyRound, color: '#3b82f6' },
  password_change_failed: { label: 'Password change failed', icon: AlertCircle, color: '#ef4444' },
  email_change: { label: 'Email changed', icon: Mail, color: '#3b82f6' },
  email_change_failed: { label: 'Email change failed', icon: AlertCircle, color: '#ef4444' },
};

function relTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function Section({ title, icon: Icon, children, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-xl p-6 shadow-sm border border-[#673c96]/10">
      <div className="flex items-center gap-2 mb-5">
        <Icon className="w-4 h-4 text-[#673c96]" />
        <h3 className="text-sm font-semibold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Alert({ kind, message }) {
  if (!message) return null;
  const color = kind === 'error'
    ? 'bg-red-50 text-red-700 border-red-200'
    : 'bg-emerald-50 text-emerald-700 border-emerald-200';
  return (
    <div className={`mb-4 px-3 py-2 rounded-lg border text-xs ${color}`} style={{ fontFamily: 'Montserrat' }}>
      {kind === 'success' && <CheckCircle className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />}
      {kind === 'error' && <AlertCircle className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />}
      {message}
    </div>
  );
}

export default function Security() {
  const { user, getAuthHeaders, checkAuth } = useAuth();
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(true);

  // Email form
  const [newEmail, setNewEmail] = useState('');
  const [emailPwd, setEmailPwd] = useState('');
  const [emailMsg, setEmailMsg] = useState({ kind: '', text: '' });
  const [emailBusy, setEmailBusy] = useState(false);

  // Password form
  const [currPwd, setCurrPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdMsg, setPwdMsg] = useState({ kind: '', text: '' });
  const [pwdBusy, setPwdBusy] = useState(false);

  const loadLog = async () => {
    try {
      const { data } = await axios.get(`${config.API}/admin/security/log`, {
        headers: getAuthHeaders(),
        
      });
      setLog(data.entries || []);
    } catch (err) { if (process.env.NODE_ENV === 'development') console.warn(err); }
    finally { setLogLoading(false); }
  };

  useEffect(() => { loadLog(); }, []); // eslint-disable-line

  const submitEmail = async (e) => {
    e.preventDefault();
    setEmailMsg({ kind: '', text: '' });
    setEmailBusy(true);
    try {
      await axios.post(`${config.API}/admin/security/change-email`,
        { current_password: emailPwd, new_email: newEmail },
        { headers: getAuthHeaders() }
      );
      setEmailMsg({ kind: 'success', text: `Email updated to ${newEmail}.` });
      setEmailPwd(''); setNewEmail('');
      await checkAuth();
      loadLog();
    } catch (err) {
      setEmailMsg({ kind: 'error', text: err?.response?.data?.detail || 'Failed to update email.' });
    } finally { setEmailBusy(false); }
  };

  const submitPwd = async (e) => {
    e.preventDefault();
    setPwdMsg({ kind: '', text: '' });
    if (newPwd !== confirmPwd) {
      setPwdMsg({ kind: 'error', text: 'New password and confirmation do not match.' });
      return;
    }
    if (newPwd.length < 10) {
      setPwdMsg({ kind: 'error', text: 'New password must be at least 10 characters.' });
      return;
    }
    setPwdBusy(true);
    try {
      await axios.post(`${config.API}/admin/security/change-password`,
        { current_password: currPwd, new_password: newPwd },
        { headers: getAuthHeaders() }
      );
      setPwdMsg({ kind: 'success', text: 'Password updated successfully.' });
      setCurrPwd(''); setNewPwd(''); setConfirmPwd('');
      loadLog();
    } catch (err) {
      setPwdMsg({ kind: 'error', text: err?.response?.data?.detail || 'Failed to update password.' });
    } finally { setPwdBusy(false); }
  };

  return (
    <div data-testid="security-page" className="space-y-6 max-w-5xl">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>
          Security
        </h1>
        <p className="text-xs text-[#673c96]/70 mt-1" style={{ fontFamily: 'Montserrat' }}>
          Logged in as <span className="font-semibold text-[#311b52]">{user?.email}</span> ({user?.role})
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Change email */}
        <Section title="Change Email" icon={Mail} testId="sec-change-email">
          <Alert kind={emailMsg.kind} message={emailMsg.text} />
          <form onSubmit={submitEmail} className="space-y-3">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#673c96]/70 mb-1.5" style={{ fontFamily: 'Montserrat' }}>New email</label>
              <input
                data-testid="sec-new-email"
                type="email" required value={newEmail} onChange={e => setNewEmail(e.target.value)}
                placeholder="new@bookmyjab.com"
                className="w-full px-3 py-2.5 rounded-lg border border-[#673c96]/15 text-sm focus:border-[#f37721] focus:ring-1 focus:ring-[#f37721]/30 outline-none transition-all"
                style={{ fontFamily: 'Montserrat' }}
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#673c96]/70 mb-1.5" style={{ fontFamily: 'Montserrat' }}>Current password</label>
              <input
                data-testid="sec-email-current-password"
                type="password" required value={emailPwd} onChange={e => setEmailPwd(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-[#673c96]/15 text-sm focus:border-[#f37721] focus:ring-1 focus:ring-[#f37721]/30 outline-none transition-all"
                style={{ fontFamily: 'Montserrat' }}
              />
            </div>
            <button
              data-testid="sec-change-email-submit"
              type="submit" disabled={emailBusy}
              className="w-full bg-[#311b52] hover:bg-[#3e195e] text-white rounded-lg py-2.5 text-xs font-semibold uppercase tracking-wider transition-all disabled:opacity-60"
              style={{ fontFamily: 'Montserrat' }}
            >
              {emailBusy ? 'Updating…' : 'Update Email'}
            </button>
          </form>
        </Section>

        {/* Change password */}
        <Section title="Update Password" icon={KeyRound} testId="sec-change-password">
          <Alert kind={pwdMsg.kind} message={pwdMsg.text} />
          <form onSubmit={submitPwd} className="space-y-3">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#673c96]/70 mb-1.5" style={{ fontFamily: 'Montserrat' }}>Current password</label>
              <input
                data-testid="sec-pwd-current"
                type="password" required value={currPwd} onChange={e => setCurrPwd(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-[#673c96]/15 text-sm focus:border-[#f37721] focus:ring-1 focus:ring-[#f37721]/30 outline-none transition-all"
                style={{ fontFamily: 'Montserrat' }}
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#673c96]/70 mb-1.5" style={{ fontFamily: 'Montserrat' }}>New password (min 10 chars)</label>
              <input
                data-testid="sec-pwd-new"
                type="password" required value={newPwd} onChange={e => setNewPwd(e.target.value)}
                minLength={10}
                className="w-full px-3 py-2.5 rounded-lg border border-[#673c96]/15 text-sm focus:border-[#f37721] focus:ring-1 focus:ring-[#f37721]/30 outline-none transition-all"
                style={{ fontFamily: 'Montserrat' }}
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#673c96]/70 mb-1.5" style={{ fontFamily: 'Montserrat' }}>Confirm new password</label>
              <input
                data-testid="sec-pwd-confirm"
                type="password" required value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)}
                minLength={10}
                className="w-full px-3 py-2.5 rounded-lg border border-[#673c96]/15 text-sm focus:border-[#f37721] focus:ring-1 focus:ring-[#f37721]/30 outline-none transition-all"
                style={{ fontFamily: 'Montserrat' }}
              />
            </div>
            <button
              data-testid="sec-change-password-submit"
              type="submit" disabled={pwdBusy}
              className="w-full bg-[#311b52] hover:bg-[#3e195e] text-white rounded-lg py-2.5 text-xs font-semibold uppercase tracking-wider transition-all disabled:opacity-60"
              style={{ fontFamily: 'Montserrat' }}
            >
              {pwdBusy ? 'Updating…' : 'Update Password'}
            </button>
          </form>
        </Section>
      </div>

      {/* Security log */}
      <Section title="Security Log — Recent 100" icon={Activity} testId="sec-log">
        {logLoading ? (
          <div className="py-8 text-center">
            <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-[#673c96] border-t-transparent" />
          </div>
        ) : log.length === 0 ? (
          <p className="text-xs text-[#673c96]/50 py-3" style={{ fontFamily: 'Montserrat' }}>No events yet.</p>
        ) : (
          <div className="divide-y divide-[#673c96]/8" data-testid="sec-log-list">
            {log.map((e, i) => {
              const cfg = eventConfig[e.event_type] || { label: e.event_type, icon: ShieldCheck, color: '#673c96' };
              const Icon = cfg.icon;
              return (
                <div key={i} className="py-3 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: `${cfg.color}12` }}>
                    <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>
                        {cfg.label}
                      </span>
                      <span className="text-[10px] text-[#673c96]/60 shrink-0" style={{ fontFamily: 'Montserrat' }}>
                        {relTime(e.created_at)}
                      </span>
                    </div>
                    <div className="text-[11px] text-[#673c96]/80 mt-0.5 truncate" style={{ fontFamily: 'Montserrat' }}>
                      {e.email} · IP {e.ip} · {(e.user_agent || '').slice(0, 80)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>
    </div>
  );
}
